/**
 * urlConfig.js - Centralized configuration for API and Socket URLs
 * 
 * This module provides a single source of truth for all API and socket
 * connection URLs used throughout the application, eliminating duplication
 * and ensuring consistency.
 */
import logger from './logger';

/**
 * Determines the base API URL based on the current environment
 * @returns {string} The base URL for API requests
 */
export const getApiBaseUrl = () => {
  // Check if running in a browser context and handle production detection
  if (typeof window !== 'undefined' && (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')) {
    // In production environment (based on hostname), use relative URL
    return '/api';
  }
  
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (isProduction) {
    // In production, use relative URL for API requests
    return '/api';
  } else {
    // In development, use the configured port from environment or default
    const devApiPort = process.env.REACT_APP_API_PORT || '9000';
    return `http://localhost:${devApiPort}/api`;
  }
};

/**
 * Determines the socket URL based on the current environment
 * @returns {string} The URL for socket connection
 */
export const getSocketUrl = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  if (isProduction) {
    // In production, use current host with appropriate protocol
     const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  } else {
    // In development, use the configured port from environment or default
    const devSocketPort = process.env.REACT_APP_SOCKET_PORT || '9000';
    return `http://localhost:${devSocketPort}`;
  }
};

/**
 * Initialize a socket connection with the provided token
 * @param {Object} io - The socket.io client library
 * @param {string} token - Authentication token
 * @returns {Object|null} Socket instance or null if no token provided
 */
export const initializeSocket = (io, token) => {
  if (!token) return null;
  
  const socketUrl = process.env.REACT_APP_SOCKET_URL || getSocketUrl();
  logger.log(`Connecting socket to: ${socketUrl}`);
  
  return io(socketUrl, {
    auth: { token },
    // Improved reconnection settings to prevent regular disconnections
    reconnection: true,           // Enable automatic reconnection
    reconnectionAttempts: 10,     // Increased from 5 to 10 attempts
    reconnectionDelay: 2000,      // Increased from 1000 to 2000ms initial delay
    reconnectionDelayMax: 10000,  // Maximum delay between reconnection attempts
    maxReconnectionAttempts: 10,  // Maximum number of reconnection attempts
    timeout: 45000,               // Connection timeout (45 seconds)
    randomizationFactor: 0.5,     // Randomization factor for reconnection delay
    // Transport and upgrade settings
    transports: ['websocket', 'polling'], // Allow both transports for fallback
    upgrade: true,                // Allow transport upgrades
    rememberUpgrade: true,        // Remember successful transport upgrades
    // Connection stability settings
    forceNew: false,              // Reuse existing connections when possible
    // Ping/pong settings (must match server)
    pingTimeout: 60000,           // Match server pingTimeout
    pingInterval: 25000,          // Match server pingInterval
    // Performance settings
    autoConnect: true,            // Automatically connect on creation
    timestampRequests: true,      // Add timestamps to requests for debugging
  });
};

/**
 * Helper function to set up authentication headers for API requests
 * @returns {Object} Headers object with auth token if available
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

/**
 * Helper to ensure auth token is included in axios requests
 * @param {Object} axios - The axios instance
 */
export const setupAxiosAuth = (axios) => {
  const token = localStorage.getItem('token');
  if (token) {
    axios.defaults.headers.common['x-auth-token'] = token;
  } else {
    // Clear token from headers if not in localStorage
    delete axios.defaults.headers.common['x-auth-token'];
    logger.warn('Authentication token not found.');
  }
};